import { oc } from "@orpc/contract";
import { z } from "zod";
import { SocketDefinitionSchema } from "@erp/workflows";
import {
  WorkflowValidationErrorSchema,
  WorkflowValidationWarningsSchema,
} from "./workflows.contract";

/**
 * Workflow JSON editing: download a workflow as a portable bundle, edit it, and
 * write it back as a new workflow or as the caller's draft. The same bundle
 * format as the editor's Import/Export and the local `workflow` CLI.
 *
 * The bundle travels as an opaque object here (not the full step schema) so
 * tool listings stay small; handlers parse it with `parseWorkflowBundle` and
 * report precise `path: message` problems.
 */

const BundleInputSchema = z
  .record(z.string(), z.unknown())
  .describe(
    "Workflow bundle JSON exactly as returned by workflows_downloadJson, after editing: { formatVersion: 1, workflow: { name, description, isActive, ... }, steps: [...], edges: [...], stepLayoutGroups: [...], zipTies: [...] }. Step types and sockets come from workflows_catalog_inspectStepType. zipTies is visual decoration; omit it to leave ties out of the file.",
  );

const WorkflowRefSchema = z
  .string()
  .min(1)
  .describe("Workflow id (uuid) or exact workflow name");

export const DownloadWorkflowJsonRequestSchema = z.object({
  workflow: WorkflowRefSchema.optional().describe(
    "Workflow id (uuid) or exact workflow name. Resolves to YOUR open draft if you have one, else the pinned version, else the newest finalized version; never another user's draft. The result's source.resolvedAs says which (your_draft, pinned, latest_finalized). Omit when passing workflowDefinitionId.",
  ),
  version: z
    .number()
    .int()
    .positive()
    .optional()
    .describe(
      "Version number within the workflow to download instead of the pinned one (only with workflow)",
    ),
  workflowDefinitionId: z
    .uuid()
    .optional()
    .describe(
      "Exact workflow definition (version) id to download, such as a draft returned by workflows_uploadDraftJson. Takes precedence over workflow and version.",
    ),
});

/** The bundle as returned: an opaque object, validated on the way back in. */
export const DownloadWorkflowJsonResponseSchema = z
  .record(z.string(), z.unknown())
  .describe("Workflow bundle JSON");

export const ValidateWorkflowJsonRequestSchema = z.object({
  bundle: BundleInputSchema,
});

export const ValidateWorkflowJsonResponseSchema = z.object({
  valid: z.boolean().describe("True when the bundle has no errors"),
  errors: z
    .array(WorkflowValidationErrorSchema)
    .describe(
      "Every problem found, each with a code, a message and the step ids (nodeIds) to look at. A malformed bundle reports code INVALID_BUNDLE.",
    ),
});

export const CreateWorkflowFromJsonRequestSchema = z.object({
  bundle: BundleInputSchema,
  name: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe("Workflow name; overrides the name inside the bundle"),
  asDraft: z
    .boolean()
    .optional()
    .describe(
      "Default true: save an unvalidated draft that only you can edit or publish (workflows_publishDraft). False validates, finalizes and pins version 1 immediately, making the workflow live.",
    ),
  autoRenameOnCollision: z
    .boolean()
    .optional()
    .describe(
      "Default true: when the name is taken, append ' (imported)' instead of failing. The original name comes back as renamedFrom.",
    ),
  managerAgentDefinitionId: z
    .uuid()
    .optional()
    .describe(
      "Manager agent definition id in this organization, overriding the one named in the bundle. Find ids with agentDefinitions_list.",
    ),
  dropManagerAgent: z
    .boolean()
    .optional()
    .describe(
      "Create the workflow without a manager agent even though the bundle names one. Changes run-time delegation; only set it on purpose.",
    ),
});

export const CreateWorkflowFromJsonResponseSchema = z.object({
  workflowId: z.uuid().describe("Id of the new workflow"),
  workflowDefinitionId: z.uuid().describe("Id of its first version"),
  version: z.number().int().describe("Version number (1)"),
  status: z
    .enum(["draft", "finalized"])
    .describe("draft when asDraft, else finalized and pinned"),
  name: z.string().describe("The name the workflow was saved under"),
  renamedFrom: z
    .string()
    .optional()
    .describe("The requested name, set only when it was taken and renamed"),
  warnings: WorkflowValidationWarningsSchema.describe(
    "Non-blocking validation findings; omitted when there are none.",
  ),
});

export const UploadWorkflowDraftJsonRequestSchema = z.object({
  workflow: WorkflowRefSchema,
  bundle: BundleInputSchema,
  remapIds: z
    .boolean()
    .optional()
    .describe(
      "Regenerate step and edge ids. Omit for automatic: ids are regenerated only when the bundle's step ids belong to another version (for example a bundle downloaded from the published version).",
    ),
  overwriteDraft: z
    .boolean()
    .optional()
    .describe(
      "Set true to replace your existing open draft with a bundle that came from a different version (such as the published one). Without it that is refused, because it discards the draft's content; download your draft with workflows_downloadJson and edit that instead.",
    ),
});

const PersistedSocketSchema = SocketDefinitionSchema.pick({
  id: true,
  name: true,
  type: true,
  required: true,
});

export const UploadWorkflowDraftJsonResponseSchema = z.object({
  workflowId: z.uuid().describe("Workflow the draft belongs to"),
  workflowDefinitionId: z
    .uuid()
    .describe("Draft version id: use it for workflows_publishDraft"),
  version: z.number().int().describe("Draft version number"),
  resumed: z
    .boolean()
    .describe("True when you already had an open draft and it was overwritten"),
  remappedIds: z
    .boolean()
    .describe("True when step and edge ids were regenerated"),
  warnings: WorkflowValidationWarningsSchema.describe(
    "Non-blocking validation findings; omitted when there are none.",
  ),
  validation: ValidateWorkflowJsonResponseSchema.describe(
    "Publish-time checks on the saved graph. The draft is saved even when invalid; fix and upload again.",
  ),
  steps: z
    .array(
      z.object({
        id: z.string().describe("Persisted step id"),
        name: z.string().describe("Step name"),
        type: z.string().describe("Step type"),
        sockets: z.object({
          inputs: z.array(PersistedSocketSchema),
          outputs: z.array(PersistedSocketSchema),
        }),
      }),
    )
    .describe(
      "The steps as saved, with server-canonical sockets. Dynamic-socket steps get regenerated socket ids, so read them here before wiring further edges.",
    ),
});

export const PublishWorkflowDraftRequestSchema = z.object({
  workflowDefinitionId: z
    .uuid()
    .describe("Draft version id from workflows_uploadDraftJson"),
  pin: z
    .boolean()
    .optional()
    .describe(
      "Default true: also pin the finalized version so triggers run it. False only finalizes it.",
    ),
  acknowledgeStaleBase: z
    .boolean()
    .optional()
    .describe(
      "Set true only after the user has agreed to proceed past a stale-base warning",
    ),
});

export const PublishWorkflowDraftResponseSchema = z.object({
  ok: z.boolean().describe("False when a stale-base warning needs a decision"),
  finalized: z
    .boolean()
    .describe(
      "True when the version is finalized (immutable). With ok false and finalized true only the pin is pending: call again with acknowledgeStaleBase after the user agrees.",
    ),
  workflowDefinitionId: z.uuid().describe("The draft version id"),
  pinned: z.boolean().describe("True when this version is now the live one"),
  stage: z
    .enum(["finalize", "pin"])
    .optional()
    .describe("Which step raised the warning (only when ok is false)"),
  warning: z
    .string()
    .optional()
    .describe("Stale-base explanation (only when ok is false)"),
  warnings: WorkflowValidationWarningsSchema.describe(
    "Non-blocking validation findings; omitted when there are none.",
  ),
});

export const workflowJsonContracts = {
  downloadJson: oc
    .input(DownloadWorkflowJsonRequestSchema)
    .output(DownloadWorkflowJsonResponseSchema)
    .route({
      summary: "Download a workflow as JSON",
      description:
        "Exports one workflow version as a portable bundle (the editor's Export format) for editing outside the app.",
      method: "GET",
    }),

  validateJson: oc
    .input(ValidateWorkflowJsonRequestSchema)
    .output(ValidateWorkflowJsonResponseSchema)
    .route({
      summary: "Validate workflow JSON",
      description:
        "Runs the publish-time graph checks on a workflow bundle and returns every error. Saves nothing.",
      method: "POST",
    }),

  createFromJson: oc
    .input(CreateWorkflowFromJsonRequestSchema)
    .output(CreateWorkflowFromJsonResponseSchema)
    .route({
      summary: "Create a workflow from JSON",
      description:
        "Creates a new workflow from a bundle, as a draft by default, renaming on a name collision.",
      method: "POST",
    }),

  uploadDraftJson: oc
    .input(UploadWorkflowDraftJsonRequestSchema)
    .output(UploadWorkflowDraftJsonResponseSchema)
    .route({
      summary: "Upload workflow JSON to a draft",
      description:
        "Creates or resumes your draft of an existing workflow and overwrites its steps and edges with a bundle's.",
      method: "POST",
    }),

  publishDraft: oc
    .input(PublishWorkflowDraftRequestSchema)
    .output(PublishWorkflowDraftResponseSchema)
    .route({
      summary: "Publish a workflow draft",
      description:
        "Finalizes your draft and, by default, pins it as the live version; reports a stale-base warning instead of forcing it.",
      method: "POST",
    }),
};
